import React, {useCallback, useContext, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {isNil, round} from 'lodash';
import styled from 'styled-components';
import {createMateriality, patchMateriality} from '../../../../../actions/Materiality/materialityactions';
import {getCurrentMateriality, getPriorYearMateriality} from '../../../../../selectors/Ares/materiality';
import {positiveIntegerRegex} from '../../../../../util/uiconstants';
import {isNullOrUndefined, labels, GetCardTypeForBody} from '../../../../../util/utils';
import {DocumentTypeIdContext} from '../../../../Main/Forms/CanvasFormContainer';
import {useDocumentId, useEngagementId, useUserPreferences} from '../../../../../util/customHooks';
import {useDecimalPattern} from '../../../../../util/Ares/customHooks';
import CanvasFormInput from '../../CanvasFormInput';
import CanvasFormTextAreaValue from '../../CanvasFormTextAreaValue';
import CanvasFormTextArea from '../../CanvasFormTextArea';
import BodyKnowledgeDescription from '../BodyKnowledgeDescription';
import {createRequestURL, createUpdatePayload} from '../Common/BodyResponseUtility';
import MaterialityDisplay from './materiality.common';
import NumberFormat from '../../../../Common/NumberFormat/NumberFormat';
import {FormBodyChangesContext} from '../FormBodyChangesWatcher';
import ErrorMessage from '../../../BaseComponentWrappers/MotifErrorMessage';
import {applyRound} from '@ey/utils';
import Icon from '../../../BaseComponentWrappers/MotifIcon';
import {IconoirWarningTriangle} from '@ey-xd/motif-icon';

export default function GuidedWorkflowPmInputBody({
	body,
	bodyId,
	getBodiesBySectionId,
	guidanceFilter,
	headerId,
	isAresView,
	isSnapshotView,
	isSummaryView,
	isDisabled,
	sectionId,
	showGuidance,
	updateBodyOptions,
	getMateriality
}) {
	const documentTypeId = useContext(DocumentTypeIdContext),
		documentId = useDocumentId(),
		{resetChanges, onBodyValueChanged} = useContext(FormBodyChangesContext);

	const engagementId = useEngagementId();

	const pmPercentageElementName = 'pm-percentage-input',
		pmAmountElementName = 'pm-amount-input',
		pmRationaleTextElementName = 'rationale-container';

	const dispatch = useDispatch();
	const formBodyDisplayData = useSelector((store) => store.formBodyDisplayData);
	const currentMateriality = useSelector(getCurrentMateriality);
	const priorYearMateriality = useSelector(getPriorYearMateriality);
	const userPreferences = useUserPreferences();

	const ammountPattern = useDecimalPattern().source;

	const basisOrForecastedAmount =
		!isNullOrUndefined(currentMateriality?.basisAdjustedAmount) && currentMateriality?.basisAdjustedAmount !== 0
			? currentMateriality.basisAdjustedAmount
			: currentMateriality?.basisForecastAmount;
	const basisAmount = !isNullOrUndefined(basisOrForecastedAmount) ? Math.abs(basisOrForecastedAmount) : null;
	const pmAmount = !isNullOrUndefined(currentMateriality?.pmAmount) ? currentMateriality.pmAmount : undefined;
	const currentRationale = !isNullOrUndefined(currentMateriality?.rationale) ? currentMateriality.rationale : undefined;

	const initialPercentageValue = pmAmount && basisAmount ? round((pmAmount / basisAmount) * 100, 1) : undefined;

	const [isLoading, setIsLoading] = useState(true);
	const [pmInputValue, setPmInputValue] = useState(pmAmount);
	const [disabled, setDisabled] = useState({});
	const [pmPercentageInputValue, setPmPercentageInputValue] = useState(initialPercentageValue);

	const highEndRange =
		!isNullOrUndefined(currentMateriality.maximumRange) && !isNullOrUndefined(basisAmount)
			? currentMateriality.maximumRange * basisAmount
			: null;
	const bufferRange = !isNullOrUndefined(highEndRange) ? applyRound(highEndRange + 0.05 * highEndRange) : null;
	const [rationale, setRationale] = useState(currentRationale);
	const [showRationaleOnLow, setShowRationaleOnLow] = useState(false);
	const [showRationaleOnHigh, setShowRationaleOnHigh] = useState(false);
	const [pmInputError, setPmInputError] = useState(
		!isNullOrUndefined(bufferRange) && pmAmount > bufferRange ? true : false
	);
	const formBodyBorder = GetCardTypeForBody(body, isAresView);

	const setShowRationaleOnLowValues = (value) => {
		setShowRationaleOnLow(value);
	};
	const setShowRationaleOnHighValues = (value) => {
		setShowRationaleOnHigh(value);
	};

	useEffect(() => {
		setPmInputError(bufferRange !== null && pmAmount > bufferRange ? true : false);
	}, [bufferRange]);

	useEffect(() => {
		if (formBodyDisplayData && formBodyDisplayData.isMaterilityUpdate && !isLoading) {
			getMateriality();
		}
	}, [formBodyDisplayData, dispatch, engagementId, isLoading]);

	const updateBody = useCallback(
		(input, newValue) => {
			let pmAmount;
			let rationaleVal = currentRationale;

			const bodyResponseObject = createUpdatePayload(body.bodyTypeId);
			updateBodyOptions(
				bodyId,
				0,
				createRequestURL(engagementId, documentId, headerId, sectionId, bodyId, documentTypeId),
				bodyResponseObject
			);

			switch (input) {
				case 'percentage': {
					if (isNullOrUndefined(newValue)) {
						newValue =
							pmPercentageInputValue && userPreferences.numberFormat === 3
								? pmPercentageInputValue.toString().replaceAll('.', ',')
								: pmPercentageInputValue;
					}
					if (newValue === '') {
						pmAmount = null;
						setPmInputValue('');
						setPmPercentageInputValue('');
					} else {
						const parsedNumber = Number.parseFloat(newValue);

						pmAmount = round((parsedNumber * basisAmount) / 100);
						setPmInputValue(Number(pmAmount));
						setPmPercentageInputValue(round((pmAmount / basisAmount) * 100, 1));
					}
					break;
				}
				case 'flat': {
					if (isNullOrUndefined(newValue)) {
						newValue = pmInputValue;
					}

					if (newValue === '') {
						pmAmount = null;
						setPmInputValue('');
						setPmPercentageInputValue('');
					} else {
						pmAmount = Number(newValue);
						if (basisAmount) {
							setPmPercentageInputValue(round((Number(newValue) / basisAmount) * 100, 1));
						}
					}
					break;
				}

				case 'rationale': {
					pmAmount = pmInputValue;
					setRationale(newValue);
					rationaleVal = newValue;
				}
			}

			if (!currentMateriality) {
				return dispatch(createMateriality(engagementId, {basisAdjustedAmount: pmAmount}));
			}

			return dispatch(
				patchMateriality(engagementId, currentMateriality.id, {
					materialityAmount: pmAmount,
					bodyTypeId: body.bodyTypeId,
					rationale: rationaleVal
				})
			).then(() => {
				switch (input) {
					case 'percentage':
						resetBodyChanges(pmPercentageElementName);
						break;
					// case 'flat':
					// 	resetBodyChanges(pmAmountElementName);
					// 	break;
					case 'rationale':
						resetBodyChanges(pmRationaleTextElementName);
						break;
				}
				return getMateriality();
			});
		},
		[
			basisAmount,
			currentMateriality,
			dispatch,
			documentId,
			engagementId,
			headerId,
			sectionId,
			bodyId,
			documentTypeId,
			pmInputValue,
			pmPercentageInputValue,
			rationale,
			currentRationale
		]
	);

	const percentageValue = !isNil(pmAmount) && basisAmount ? round((pmAmount / basisAmount) * 100, 1) : '';
	const oneInput = basisAmount ? false : true;

	function inputHandleOnEdit(id, value = true) {
		const toHandle = id !== pmPercentageElementName ? pmPercentageElementName : pmAmountElementName;
		setDisabled((state) => ({...state, [toHandle]: value}));
	}

	function inputHandleOnChanged(id) {
		inputHandleOnEdit(id, false);
	}

	function inputHandleOnCancel(id) {
		inputHandleOnEdit(id, false);
		onInputCancel(id);
	}

	function percentageInputHandleOnCancel() {
		inputHandleOnEdit(pmPercentageElementName, false);
		onInputCancel(pmPercentageElementName);
	}

	const saveChanges = (e) => {
		let value = e.currentTarget.value;
		setRationale(value);
		onInputChange(currentRationale, value, pmRationaleTextElementName);
	};

	const inputHandleOnAccept = useCallback(
		(id, value) => {
			let realValue = value;
			if (userPreferences.numberFormat === 3) {
				realValue = realValue.replaceAll(',', '.');
			}

			const newValue = realValue !== '' ? Number(realValue) : realValue;

			switch (id) {
				case pmPercentageElementName: {
					const parsedNum = Number.parseFloat(newValue);
					if (!isNullOrUndefined(bufferRange)) {
						let inputAmount = round((parsedNum * basisAmount) / 100);
						if (inputAmount > bufferRange) {
							setPmInputError(true);
							return Promise.resolve();
						}
					}
					setPmInputError(false);
					setPmPercentageInputValue(newValue);
					return updateBody('percentage', newValue).then(() => {
						resetBodyChanges(pmAmountElementName);
					});
				}
				case pmAmountElementName: {
					if (!isNullOrUndefined(bufferRange) && newValue > bufferRange) {
						setPmInputError(true);
						return Promise.resolve();
					}
					setPmInputError(false);
					setPmInputValue(newValue);
					return updateBody('flat', newValue).then(() => {
						resetBodyChanges(pmAmountElementName);
					});
				}
				case 'rationale-input': {
					setRationale(value);
					return updateBody('rationale', value).then(() => {
						resetBodyChanges(pmRationaleTextElementName);
					});
				}
				default: {
					return Promise.resolve();
				}
			}
		},
		[updateBody, bufferRange, basisAmount, onInputCancel]
	);

	const onInputChange = useCallback(
		(initialValue, newValue, elementName) => {
			onBodyValueChanged(bodyId, initialValue, newValue, elementName);

			if (elementName === pmPercentageElementName) {
				setPmPercentageInputValue(newValue);
			}
			if (elementName === pmAmountElementName) {
				setPmInputValue(newValue);
			}
		},
		[onBodyValueChanged, bodyId]
	);

	const resetBodyChanges = useCallback(
		(elementName) => {
			resetChanges(bodyId, elementName);
		},
		[resetChanges, bodyId]
	);

	const onInputCancel = useCallback(
		(elementName) => {
			resetBodyChanges(elementName);
			switch (elementName) {
				case pmAmountElementName: {
					setPmInputValue(pmAmount);
					break;
				}
				case pmPercentageElementName: {
					setPmPercentageInputValue(initialPercentageValue);
					break;
				}
				case pmRationaleTextElementName: {
					setRationale(currentRationale);
				}
			}
			setPmInputError(false);
		},
		[resetBodyChanges, pmAmount, initialPercentageValue, currentRationale, bodyId]
	);

	return (
		<StyledGuidedWorkflowPmInputBody
			className={`StyledGuidedWorkflowPmInputBody aresSectionBodyWrapper SectionBodyWrapper ${formBodyBorder}`}
			oneInput={oneInput}
		>
			{body && (
				<BodyKnowledgeDescription
					isAresView={isAresView}
					showGuidance={showGuidance}
					guidanceFilter={guidanceFilter}
					isSnapshotView={isSnapshotView}
					bodyModel={body}
					engagementId={engagementId}
					documentId={documentId}
					headerId={headerId}
					sectionId={sectionId}
					bodyId={bodyId}
					bodyDescriptionText={body.name}
					isCustom={body.isCustom}
					isDuplicate={body.isDuplicate}
					getBodiesBySectionId={getBodiesBySectionId}
					isViewBodyOnly={isSummaryView}
				/>
			)}
			<StyledSectionBodyPMData className="SectionBodyData aresSectionBodyData SectionBodyPMData">
				<MaterialityDisplay.MeasurementBasis {...currentMateriality} />
				<MaterialityDisplay.BasisAdjustedAmount {...currentMateriality} basisAmount={basisAmount} />
				<MaterialityDisplay.Range {...currentMateriality} basisAmount={basisAmount} />
				<MaterialityDisplay.SuggestedRange
					{...currentMateriality}
					basisAmount={basisAmount}
					setShowRationaleOnLowValues={setShowRationaleOnLowValues}
					setShowRationaleOnHighValues={setShowRationaleOnHighValues}
				/>
				<MaterialityDisplay.PriorPeriodPM {...priorYearMateriality} />
				<StyledPMInputContainer className={`input-container PMInputContainer`}>
					<section className="PMInputWrapper">
						<section className="pmPercentageInputWrapper">
							{basisAmount !== undefined && !isNil(basisAmount) && (
								<section
									className={`${pmInputValue === undefined || pmInputValue === '' ? 'pmInputValue' : ''} ${
										pmInputError ? 'pmInputErrorWrapper' : ''
									}`}
								>
									<CanvasFormInput
										disabled={disabled[pmPercentageElementName] || isDisabled}
										id={pmPercentageElementName}
										label={labels.pmBasisPercentage}
										maxLength={15}
										onAccept={(value) => inputHandleOnAccept(pmPercentageElementName, value)}
										onCancel={percentageInputHandleOnCancel}
										onChange={(currentValue) =>
											onInputChange(initialPercentageValue, currentValue, pmPercentageElementName)
										}
										onChanged={() => inputHandleOnChanged(pmPercentageElementName)}
										onEdit={() => inputHandleOnEdit(pmPercentageElementName)}
										placeHolder={labels.pmBasisPercentage}
										showLabel
										type="percentage"
										saveInput={false}
										value={
											(pmPercentageInputValue && userPreferences.numberFormat === 3) || pmPercentageInputValue === 0
												? pmPercentageInputValue.toString().replaceAll('.', ',')
												: pmPercentageInputValue
										}
									/>
								</section>
							)}
							<section className={`${pmInputValue === undefined || pmInputValue === '' ? 'pmInputValue' : ''}`}>
								<CanvasFormInput
									disabled={disabled[pmAmountElementName] || isDisabled}
									id={pmAmountElementName}
									name={pmAmountElementName}
									label={pmAmount > 0 ? labels.pmAmount : labels.pmAmountPlaceholder}
									maxLength={15}
									onAccept={(value) => inputHandleOnAccept(pmAmountElementName, '' + value + '')}
									onCancel={() => inputHandleOnCancel(pmAmountElementName)}
									onChange={(currentValue) => onInputChange(pmAmount, currentValue, pmAmountElementName)}
									onChanged={() => inputHandleOnChanged(pmAmountElementName)}
									onEdit={() => inputHandleOnEdit(pmAmountElementName)}
									pattern={ammountPattern}
									showLabel
									type="number"
									saveInput={false}
									value={pmInputValue}
								/>
							</section>
						</section>
						{pmInputError ? (
							<section className="pmValidationMessageWrapper">
								<Icon src={IconoirWarningTriangle} />
								<ErrorMessage
									isAresView={isAresView}
									className="pmValidationMessage validationErrorMessage"
									message={labels.pmValidationMessage}
								/>
							</section>
						) : (
							''
						)}
					</section>
					{((!isNullOrUndefined(pmAmount) && showRationaleOnLow) ||
						showRationaleOnHigh ||
						(!isNullOrUndefined(pmAmount) &&
							!isNullOrUndefined(rationale) &&
							isNullOrUndefined(currentMateriality.suggestedPMBracket))) && (
						<section className="rationale-input">
							<span className="rationaleDescriptionText">{labels.rationaleDescription}</span>
							<CanvasFormTextArea
								id={pmRationaleTextElementName}
								name={pmRationaleTextElementName}
								label={labels.enterRationale}
								onAccept={(value) => inputHandleOnAccept('rationale-input', value)}
								onChange={saveChanges}
								onCancel={() => inputHandleOnCancel(pmRationaleTextElementName)}
								showLabel
								maxLength={500}
								progressBarClassName="saverationale-loader"
								value={currentRationale}
								isAresView={isAresView}
								disabled={isDisabled}
							>
								<CanvasFormTextAreaValue isSnapshotView={false} />
							</CanvasFormTextArea>
						</section>
					)}
				</StyledPMInputContainer>
			</StyledSectionBodyPMData>
		</StyledGuidedWorkflowPmInputBody>
	);
}

const StyledPMInputContainer = styled.section`
	position: relative;
	display: flex;
	flex-direction: column;
	margin-top: var(--px-16);
	.PMInputWrapper {
		display: flex;
		flex-direction: column;
		.pmPercentageInputWrapper {
			display: flex;
			.pmInputValue {
				.canvas-form-input {
					.motif-form-field {
						.StyledCanvasFormInputDisplay {
							.motif-label {
								top: var(--px-10);
							}
						}
					}
				}
			}
			.motif-error-message {
				align-items: flex-start;
			}
		}
		.pmValidationMessageWrapper {
			display: flex;
			.motif-icon {
				display: inline-flex;
				margin-top: var(--px-10);
				margin-right: var(--px-3);
				color: var(--red-600);
				svg {
					width: var(--px-20) !important;
					height: var(--px-20) !important;
					margin-top: var(--px-2);
					fill: var(--red-600);
					path {
						fill: var(--red-600);
					}
				}
			}
			.pmValidationMessage {
				width: var(--px-320);
				&.validationErrorMessage {
					color: var(--red-600);
					width: var(--px-400);
					margin-top: var(--px-10) !important;
					display: flex;
					align-items: center;
					.motif-icon {
						color: var(--error) !important;
						display: inline-flex;
						align-items: center;
						margin-right: var(--px-3);
						svg {
							width: var(--px-20) !important;
							height: var(--px-20) !important;
							cursor: default;
							fill: var(--red-600) !important;
							path {
								fill: var(--red-600);
							}
						}
					}
					.errorText {
						color: var(--neutrals-900);
						font-size: var(--px-12) !important;
						font-weight: 300;
						line-height: var(--px-20);
						margin: 0;
					}
				}
			}
		}
		.canvas-form-input {
			.motif-form-field {
				.motif-label {
					margin-bottom: 0;
				}
				.StyledCanvasFormInputDisplay {
					border: 0;
					height: var(--px-45);
					.motif-label {
						margin-bottom: 0;
						top: 0;
						position: absolute;
					}
				}
			}
			.StyledConfirmInputIcons {
				height: var(--px-45);
			}
		}
	}
	.PMInputContainer {
		margin-top: var(--px-32);
		.pmPercentageInputWrapper {
			.pmInputErrorWrapper {
				.StyledCanvasFormInput {
					border: var(--px-1) solid var(--neutrals-200);
				}
			}
		}
	}
	.rationale-input {
		margin: var(--px-24) 0 var(--px-10) 0;
		.rationaleDescriptionText {
			font-size: var(--px-14);
			color: var(--neutrals-900);
		}
		.canvas-form-textarea {
			width: var(--px-656);
			margin-top: var(--px-8);
		}
	}
	.canvas-form-input {
		width: var(--px-320) !important;
		.motif-form-field {
			width: var(--px-320) !important;
		}
	}
	.rationale-container {
		display: inline;
		position: relative;
		width: inherit;
		margin-top: var(--px-10);
		margin-bottom: var(--px-10);
	}
`;

const StyledSectionBodyPMData = styled.section`
	margin-top: var(--px-15);
	&.aresSectionBodyData {
		position: relative;
		margin-top: var(--px-24);
		padding: var(--px-24);
		border-radius: var(--px-12) !important;
		border: var(--px-1) solid var(--neutrals-200);
		background-color: var(--neutrals-00white);
	}
`;

const StyledGuidedWorkflowPmInputBody = styled.section`
	display: block;
`;
